/* The church's own signup fields — the client's "Add field".
 *
 * A church asks its members things the app cannot guess: Baptised? Ministry
 * involvement? Date of baptism? These six types are the client's list, in
 * their order and their wording.
 *
 * TWO MODES, because the two screens differ in what is possible:
 *
 *   drafting  — a church being created. Fields are held locally and sent with
 *               it, so they can still be removed.
 *   saved     — a church that exists. Fields can be ADDED but not removed:
 *               people have already answered them, and deleting the question
 *               would orphan the answers. The API has no delete endpoint for
 *               exactly that reason.
 */
import { useState } from 'react'
import { Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field, SelectField } from '@/components/ui/field'
import type {
  ChurchFeatures,
  CustomField,
  CustomFieldDraft,
  CustomFieldType,
} from '@/types/api'

/** The client's six types, in the order their document lists them. */
const FIELD_TYPES: [CustomFieldType, string][] = [
  ['text_box', 'Text box'],
  ['check_box', 'Check box'],
  ['drop_down', 'Drop down (list)'],
  ['date_time', 'Date time'],
  ['image', 'Image'],
  ['number', 'Number'],
]

const TYPE_LABEL = Object.fromEntries(FIELD_TYPES) as Record<CustomFieldType, string>

export function CustomFieldsEditor({
  fields,
  onAdd,
  onRemove,
  busy = false,
  error = null,
  showList = true,
}: {
  fields: (CustomField | CustomFieldDraft)[]
  onAdd: (field: CustomFieldDraft) => void
  /** Omitted for a saved church — see the note above. */
  onRemove?: (index: number) => void
  busy?: boolean
  error?: string | null
  /** False where the screen draws its own list — the church's own page,
   *  which can also edit, reorder and retire. */
  showList?: boolean
}) {
  const [label, setLabel] = useState('')
  const [type, setType] = useState<CustomFieldType>('text_box')
  const [required, setRequired] = useState(false)
  const [options, setOptions] = useState('')

  const parsedOptions = options
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean)

  // A drop down with no options is a control nobody can answer, and the API
  // rejects it — so the button says no before the round trip does.
  const ready =
    label.trim().length > 0 && (type !== 'drop_down' || parsedOptions.length > 0)

  function add() {
    if (!ready) return
    onAdd({
      label: label.trim(),
      field_type: type,
      is_required: required,
      options: type === 'drop_down' ? parsedOptions : null,
    })
    setLabel('')
    setType('text_box')
    setRequired(false)
    setOptions('')
  }

  return (
    <div className="space-y-s4">
      {/* One bordered block, so it is obvious that the label, the type, the
          options and Required all describe the SAME field being defined —
          spread across the card they read as four unrelated controls, and
          "Required" appears to apply to the whole form. */}
      <fieldset className="rounded border border-line-2 bg-surface-2 p-s3">
        <legend className="px-s2 text-sm font-semibold text-ink-2">
          New field
        </legend>

        <div className="grid gap-s3 sm:grid-cols-2">
          <Field
            label="Question"
            size="sm"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            help="What the member is asked, e.g. Baptised"
            onKeyDown={(e) => {
              // Enter adds the field rather than submitting the church — this
              // editor sits inside the create form, and a stray Enter here
              // would create the church with the field still unadded.
              if (e.key === 'Enter') {
                e.preventDefault()
                add()
              }
            }}
          />
          <SelectField
            label="Answer type"
            size="sm"
            value={type}
            onChange={(e) => setType(e.target.value as CustomFieldType)}
            options={FIELD_TYPES.map(([value, l]) => ({ value, label: l }))}
          />

          {type === 'drop_down' ? (
            <Field
              label="Choices (comma separated)"
              className="sm:col-span-2"
              size="sm"
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              help="e.g. Choir, Youth, Ushering"
            />
          ) : null}
        </div>

        <div className="mt-s3 flex flex-wrap items-center justify-between gap-s3">
          <label className="flex items-center gap-s2">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="h-4 w-4 accent-[var(--cn-brand)]"
            />
            <span className="text-base">
              A member must answer this to finish signing up
            </span>
          </label>

          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={add}
            disabled={!ready || busy}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add field
          </Button>
        </div>
      </fieldset>

      {error ? <p className="text-base text-danger">{error}</p> : null}

      {!showList ? null : fields.length ? (
        <ul className="divide-y divide-line-2 rounded border border-line-2">
          {fields.map((f, i) => (
            <li key={'id' in f ? f.id : `${f.label}-${i}`} className="flex items-center gap-s3 px-s3 py-s2">
              <span className="min-w-0 flex-1 truncate font-semibold">
                {f.label}
                {f.is_required ? (
                  <span className="text-danger" aria-label="required">
                    {' '}
                    *
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 rounded-pill bg-surface-2 px-s3 py-0.5 text-sm text-ink-2">
                {TYPE_LABEL[f.field_type]}
              </span>
              {f.field_type === 'drop_down' && f.options?.length ? (
                <span className="hidden shrink-0 text-sm text-ink-2 sm:inline">
                  {f.options.join(' · ')}
                </span>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  aria-label={`Remove ${f.label}`}
                  className="shrink-0 rounded p-1 text-ink-2 hover:bg-hover hover:text-danger"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-base text-ink-3">
          No custom fields yet — members fill in the standard signup form only.
        </p>
      )}

      {showList && !onRemove && fields.length ? (
        <p className="text-sm text-ink-3">
          Fields cannot be removed once the church exists — members have already
          answered them.
        </p>
      ) : null}
    </div>
  )
}

/** The plan names the client priced, rather than a bare "Basic".
 *  Shared so the create form and the detail form cannot disagree. */
export const PLAN_OPTIONS = [
  { value: 'basic', label: 'Basic — ₹999/mo · up to 500 members' },
  { value: 'standard', label: 'Standard — ₹1,999/mo · up to 1,000' },
  { value: 'professional', label: 'Professional — ₹14,999/mo · unlimited' },
]

/** A church's seven feature switches, in order. Shared by the create form and
 *  the church's Features tab so the two cannot word them differently. */
export const FEATURE_LABELS: [keyof ChurchFeatures, string][] = [
  ['send_individual_messages', 'Send individual messages'],
  ['send_bulk_msgs', 'Send bulk messages'],
  ['audio_conf_calls', 'Audio conference calls'],
  ['video_conf_calls', 'Video conference calls'],
  ['live_broadcasts', 'Go live'],
  ['events', 'Events'],
  ['polls', 'Polls'],
]
